<template>
    <v-container fluid class="pa-0 availability-page">
        <v-row no-gutters>
            <!-- Left: Availability editor -->
            <v-col cols="12" md="4" lg="3" class="pa-4">
                <v-card class="side-nav-card" outlined>
                    <v-card-title class="py-3 px-4 flex-column align-stretch">
                        <div class="d-flex align-center w-100 flex-wrap">
                            <span class="side-nav-title">Your weekly availability</span>
                            <v-spacer />
                            <v-btn
                                small
                                color="primary"
                                :loading="saving"
                                :disabled="loading || !hasUnsavedChanges"
                                @click="saveAvailability"
                            >
                                Save
                            </v-btn>
                        </div>
                        <p class="side-nav-subtitle mb-0 mt-2">
                            Choose when you are generally free for lessons. Times can span midnight (e.g. 1:00&nbsp;PM–1:00&nbsp;AM).
                            The calendar on the right updates after you save.
                        </p>
                        <v-chip
                            v-if="hasUnsavedChanges"
                            x-small
                            class="mt-2 align-self-start"
                            color="amber darken-2"
                            text-color="white"
                        >
                            Unsaved changes
                        </v-chip>
                    </v-card-title>
                    <v-divider />
                    <v-card-text class="pt-2 pb-3 px-4">
                        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
                        <v-list dense class="pt-0 availability-list">
                            <v-list-item v-for="day in draftDays" :key="day.key" class="px-0 availability-list-item">
                                <v-list-item-content>
                                    <div class="day-label mb-1">{{ day.label }}</div>
                                    <div class="d-flex align-center time-range-row flex-wrap">
                                        <v-select
                                            v-model="day.start"
                                            :items="timeOptions"
                                            outlined
                                            dense
                                            hide-details
                                            clearable
                                            placeholder="Start"
                                            class="time-input"
                                            aria-label="Start time"
                                        />
                                        <span class="time-range-separator">–</span>
                                        <v-select
                                            v-model="day.end"
                                            :items="timeOptions"
                                            outlined
                                            dense
                                            hide-details
                                            clearable
                                            placeholder="End"
                                            class="time-input"
                                            aria-label="End time"
                                        />
                                    </div>
                                    <div class="preset-row d-flex flex-wrap align-center">
                                        <span class="preset-label mr-1">Quick fill:</span>
                                        <v-chip
                                            v-for="p in timePresets"
                                            :key="day.key + p.key"
                                            x-small
                                            outlined
                                            class="ma-0 mr-1 mb-1"
                                            @click="applyPreset(day.key, p.key)"
                                        >
                                            {{ p.label }}
                                        </v-chip>
                                        <v-btn x-small text class="mb-1 px-1" @click="clearDay(day.key)">Clear</v-btn>
                                    </div>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Right: Weekly calendar (saved state only) -->
            <v-col cols="12" md="8" lg="9" class="pa-4 calendar-wrapper">
                <StudentAvailabilityWeekDisplay :days="days" :loading="loading" />
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import Parse from 'parse'
import StudentAvailabilityWeekDisplay from '@/components/student/StudentAvailabilityWeekDisplay.vue'
import {
  DAY_ORDER,
  defaultDays,
  mapAvailabilityRowsToDays,
  to12hFromMinutes
} from '@/utils/studentAvailabilityShared'

export default {
  name: 'StudentAvailability',
  components: { StudentAvailabilityWeekDisplay },
  data () {
    return {
      loading: true,
      saving: false,
      days: defaultDays(),
      draftDays: defaultDays()
    }
  },
  created () {
    this.loadAvailability()
  },
  computed: {
    timeOptions () {
      return Array.from({ length: 48 }, (_, i) => to12hFromMinutes(i * 30))
    },
    timePresets () {
      return [
        { key: 'morning', label: 'Morning' },
        { key: 'afternoon', label: 'Afternoon' },
        { key: 'evening', label: 'Evening' },
        { key: 'allday', label: 'All day' }
      ]
    },
    hasUnsavedChanges () {
      return this.serializeDays(this.draftDays) !== this.serializeDays(this.days)
    }
  },
  methods: {
    serializeDays (list) {
      return JSON.stringify(
        (list || []).map((d) => ({ key: d.key, start: d.start || '', end: d.end || '' }))
      )
    },
    cloneDays (list) {
      return (list || []).map((d) => ({ ...d }))
    },
    toMinutesFrom12h (value) {
      if (value === null || value === undefined || value === '') return null
      const trimmed = String(value).trim()
      const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
      if (!match) return null
      let h = Number(match[1])
      const m = Number(match[2])
      const suf = match[3].toUpperCase()
      if (h < 1 || h > 12 || m < 0 || m > 59 || Number.isNaN(h) || Number.isNaN(m)) return null
      if (suf === 'PM' && h !== 12) h += 12
      if (suf === 'AM' && h === 12) h = 0
      return h * 60 + m
    },
    presetRange (key) {
      const opts = this.timeOptions
      const first = opts[0]
      const last = opts[opts.length - 1]
      switch (key) {
        case 'morning':
          return { start: '7:00 AM', end: '12:00 PM' }
        case 'afternoon':
          return { start: '12:00 PM', end: '5:00 PM' }
        case 'evening':
          return { start: '5:00 PM', end: '9:00 PM' }
        case 'allday':
          return { start: first, end: last }
        default:
          return { start: '', end: '' }
      }
    },
    applyPreset (dayKey, presetKey) {
      const d = this.draftDays.find((x) => x.key === dayKey)
      if (!d) return
      const r = this.presetRange(presetKey)
      d.start = r.start
      d.end = r.end
    },
    clearDay (dayKey) {
      const d = this.draftDays.find((x) => x.key === dayKey)
      if (!d) return
      d.start = ''
      d.end = ''
    },
    async loadAvailability () {
      const user = Parse.User.current()
      if (!user) {
        this.loading = false
        return
      }

      this.loading = true
      const Availability = Parse.Object.extend('StudentAvailability')
      const query = new Parse.Query(Availability)
      query.equalTo('studentId', user)
      query.limit(20)

      try {
        const rows = await query.find()
        this.days = mapAvailabilityRowsToDays(rows)
        this.draftDays = this.cloneDays(this.days)
      } catch (err) {
        console.error('loadAvailability error:', err)
      } finally {
        this.loading = false
      }
    },
    async saveAvailability () {
      const user = Parse.User.current()
      if (!user) return

      for (const day of this.draftDays) {
        if (!day.start && !day.end) continue
        if (!day.start || !day.end) {
          this.$store.dispatch('toggleDialog', {
            status: true,
            title: 'Incomplete time range',
            message: `${day.label}: choose both a start and end time, or clear both.`
          })
          return
        }
        const s = this.toMinutesFrom12h(day.start)
        const e = this.toMinutesFrom12h(day.end)
        if (s === null || e === null) {
          this.$store.dispatch('toggleDialog', {
            status: true,
            title: 'Invalid time',
            message: `${day.label}: use the time dropdowns so values stay valid.`
          })
          return
        }
        if (s === e) {
          this.$store.dispatch('toggleDialog', {
            status: true,
            title: 'Invalid range',
            message: `${day.label}: start and end cannot be the same. For overnight, end should be on the next morning (e.g. 1:00 AM).`
          })
          return
        }
      }

      this.saving = true
      const Availability = Parse.Object.extend('StudentAvailability')

      try {
        const existingQuery = new Parse.Query(Availability)
        existingQuery.equalTo('studentId', user)
        existingQuery.limit(20)
        const existingRows = await existingQuery.find()

        const existingByDay = {}
        existingRows.forEach((row) => {
          existingByDay[row.get('dayKey')] = row
        })

        const saveList = []
        const deleteList = []

        this.draftDays.forEach((day) => {
          const hasValue = day.start && day.end
          const existing = existingByDay[day.key]

          if (!hasValue) {
            if (existing) deleteList.push(existing)
            return
          }

          const s = this.toMinutesFrom12h(day.start)
          const e = this.toMinutesFrom12h(day.end)
          const overnight = e < s

          const row = existing || new Availability()
          row.set('studentId', user)
          row.set('dayKey', day.key)
          row.set('dayLabel', day.label)
          row.set('dayIndex', DAY_ORDER.indexOf(day.key))
          row.set('startMinutes', s)
          row.set('endMinutes', e)
          row.set('startTime', day.start)
          row.set('endTime', day.end)
          row.set('isOvernight', overnight)
          row.set('active', true)
          saveList.push(row)
        })

        if (deleteList.length) {
          await Parse.Object.destroyAll(deleteList)
        }
        if (saveList.length) {
          await Parse.Object.saveAll(saveList)
        }

        this.days = this.cloneDays(this.draftDays)

        this.$store.dispatch('toggleDialog', {
          status: true,
          title: 'Saved',
          message: 'Your availability was saved. The calendar now matches what you entered.'
        })
      } catch (err) {
        console.error('saveAvailability error:', err)
        this.$store.dispatch('toggleDialog', {
          status: true,
          title: 'Error',
          message: 'Unable to save availability. Please try again.'
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.time-range-row {
    gap: 8px;
}

.time-input {
    flex: 1 1 120px;
    min-width: 0;
}

.preset-row {
    margin-top: 6px;
}

.preset-label {
    font-size: 0.7rem;
    color: rgba(0, 0, 0, 0.55);
    width: 100%;
    flex-basis: 100%;
}

@media (min-width: 400px) {
    .preset-label {
        width: auto;
        flex-basis: auto;
    }
}

.side-nav-subtitle {
    font-size: 0.8rem;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.62);
    line-height: 1.35;
}

.availability-list-item {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.availability-list-item:last-child {
    border-bottom: none;
}

.availability-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #2e7d32 0%, #43a047 40%, #66bb6a 100%);
    padding-bottom: calc(56px + env(safe-area-inset-bottom, 0px) + 24px);
}

@media (min-width: 960px) {
    .availability-page {
        padding-bottom: 0;
    }
}

.side-nav-card {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.side-nav-title {
    font-weight: 700;
    font-size: 1.05rem;
    line-height: 1.3;
}

.day-label {
    font-weight: 600;
    font-size: 0.9rem;
}

.time-range-separator {
    flex: 0 0 auto;
    font-size: 0.9rem;
    padding: 0 2px;
}

.calendar-wrapper {
    overflow: hidden;
}

@media (max-width: 959px) {
    .calendar-wrapper {
        overflow: visible;
    }
}
</style>
